import apiClient from './apiClient';
import type { ObservationMedia, ObservationMediaUploadUrlResponse } from '../types/observationMedia';

export async function getObservationMedia(wodKey: string): Promise<ObservationMedia[]> {
  const res = await apiClient.get<ObservationMedia[]>(`/workorderdetails/${wodKey}/observation-media`);
  return res.data;
}

export async function requestUploadUrl(
  wodKey: string,
  file: {
    FileName: string; FileSize: number; MimeType: string;
    Caption?: string | null; DurationSeconds?: number | null;
    /** Field-scoped evidence: '<testTypeId>.<fieldKey>'. Omit for test-level media. */
    FieldKey?: string | null;
  },
): Promise<ObservationMediaUploadUrlResponse> {
  const res = await apiClient.post<ObservationMediaUploadUrlResponse>(
    `/workorderdetails/${wodKey}/observation-media/upload-url`,
    file,
  );
  return res.data;
}

/** Direct browser → Azure Blob PUT via SAS URL (no server proxying). */
export function uploadToBlob(
  uploadUrl: string,
  file: Blob,
  mimeType: string,
  onProgress?: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl, true);
    xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
    xhr.setRequestHeader('Content-Type', mimeType || 'application/octet-stream');

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed with status ${xhr.status}`));
    };

    xhr.onerror = () => reject(new Error('Upload failed'));
    xhr.send(file);
  });
}

export async function confirmUpload(observationMediaKey: string): Promise<ObservationMedia> {
  const res = await apiClient.post<ObservationMedia>(
    `/workorderdetails/observation-media/${observationMediaKey}/confirm`,
  );
  return res.data;
}

export async function updateCaption(observationMediaKey: string, caption: string | null): Promise<ObservationMedia> {
  const res = await apiClient.put<ObservationMedia>(
    `/workorderdetails/observation-media/${observationMediaKey}`,
    { Caption: caption },
  );
  return res.data;
}

export async function removeObservationMedia(observationMediaKey: string): Promise<void> {
  await apiClient.delete(`/workorderdetails/observation-media/${observationMediaKey}`);
}

export interface FieldEvidenceSummaryRow {
  WorkOrderDetailKey: string;
  FieldKey: string;
}

/**
 * Batched "which fields of which tests have evidence" lookup for listing
 * pages. Chunked to stay under the backend's 500-key cap.
 */
export async function fetchFieldEvidenceSummary(wodKeys: string[]): Promise<FieldEvidenceSummaryRow[]> {
  const out: FieldEvidenceSummaryRow[] = [];
  for (let i = 0; i < wodKeys.length; i += 400) {
    const chunk = wodKeys.slice(i, i + 400);
    // eslint-disable-next-line no-await-in-loop
    const res = await apiClient.post<FieldEvidenceSummaryRow[]>(
      '/workorderdetails/observation-media/field-summary',
      { wodKeys: chunk },
    );
    out.push(...res.data);
  }
  return out;
}
